import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import ActionsMenu from "./ActionsMenu";
import Footer from "../../../examples/Footer";

export default function ExamType() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allRows, setAllRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = Boolean(anchorEl);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;

  const [form, setForm] = useState({
    exam_type_name: "",
    description: "",
    status: "ACTIVE",
    created_by: "",
  });

  // ✅ Snackbar
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ✅ Fetch Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}`);
      if (data.success) {
        const mappedRows = data.data.items.map((r) => ({
          id: r.exam_type_id,
          ...r,
          created_on: r.created_on
            ? new Date(r.created_on).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
        }));
        setAllRows(mappedRows);
        setRows(mappedRows);
        setRowCount(mappedRows.length);
      } else {
        showSnackbar(data.message || "Failed to load data", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // ✅ Search filter
  useEffect(() => {
    if (!search) {
      setRows(allRows);
    } else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.exam_type_name?.toLowerCase().includes(s) ||
            r.description?.toLowerCase().includes(s) ||
            r.created_by?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, allRows]);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({ exam_type_name: "", description: "", status: "ACTIVE", created_by: "" });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setForm({
      exam_type_name: row.exam_type_name,
      description: row.description || "",
      status: row.status,
      created_by: row.created_by,
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this exam type?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}/${row.exam_type_id}`
      );
      fetchData();
      showSnackbar("Exam type deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Delete failed", "error");
    }
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      client_id: Number(clientId),
      college_id: Number(collegeId),
      created_by: user_email,
    };

    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}/${editing.id}`,
          payload
        );
        showSnackbar("Exam type updated successfully", "success");
      } else {
        await axios.post(`${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}`, payload);
        showSnackbar("Exam type created successfully", "success");
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Save failed", "error");
    }
  };
  // ✅ Upload CSV
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = ""; // reset file input
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 70,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      {
        field: "exam_type_id",
        headerName: "ID",
        flex: 0.5,
        minWidth: 50,
        valueGetter: (p) => p.row.id,
      },
      { field: "exam_type_name", headerName: "Exam Type", flex: 1, minWidth: 150 },
      { field: "description", headerName: "Description", flex: 1, minWidth: 150 },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <span style={{ color: params.value === "ACTIVE" ? "green" : "red" }}>{params.value}</span>
        ),
      },
      { field: "created_on", headerName: "Created On", flex: 1, minWidth: 120 },
      { field: "created_by", headerName: "Created By", flex: 1, minWidth: 120 },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <ActionsMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleDelete} />
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            EXAM TYPES
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            Actions
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                ADD EXAM TYPE
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  const link = document.createElement("a");
                  link.href = `${process.env.PUBLIC_URL}/examtypes.csv`;
                  link.setAttribute("download", "examtypes.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleMenuClose();
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="medium"
            placeholder="Search exam types"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, mr: 2, ml: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            autoHeight
            pageSize={pageSize} // controlled pageSize
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
          />
        </Box>
      </Paper>

      {/* Dialog Add/Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Exam Type" : "Add Exam Type"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Exam Type Name"
              value={form.exam_type_name}
              onChange={(e) => setForm({ ...form, exam_type_name: e.target.value })}
              required
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              maxRows={4}
            />
            <TextField
              label="Status"
              select
              fullWidth
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
